import { Box, Link, Typography } from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import type { Activation } from "../campaignTypes";
import {
  PORTAL_MAX_WIDTH,
  bodySx,
  cardSurfaceSx,
  eyebrowSx,
  portalFont,
  sectionTitleSx,
} from "../partnerTheme";

export default function ActivationCards({
  eyebrow,
  title,
  description,
  activations,
}: {
  eyebrow: string;
  title: string;
  description: string;
  activations: Activation[];
}) {
  if (!activations.length) return null;

  return (
    <Box
      id="delivered"
      component="section"
      sx={{
        maxWidth: PORTAL_MAX_WIDTH,
        mx: "auto",
        px: { xs: 2, md: 4 },
        py: { xs: 6, md: 10 },
        scrollMarginTop: 96,
      }}
    >
      {eyebrow ? <Typography sx={eyebrowSx}>{eyebrow}</Typography> : null}
      <Typography sx={sectionTitleSx}>{title}</Typography>
      {description ? (
        <Typography sx={{ ...bodySx, maxWidth: 640, mb: 4 }}>{description}</Typography>
      ) : null}
      <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 2.5, md: 3 } }}>
        {activations.map((activation) => (
          <ActivationCard key={activation.id} activation={activation} />
        ))}
      </Box>
    </Box>
  );
}

export function ActivationCard({ activation }: { activation: Activation }) {
  const photoCount = activation.photos.length;
  const hasLineup = Boolean(activation.talent?.trim());
  const hasMetrics = activation.metrics.length > 0;

  return (
    <Box sx={{ ...cardSurfaceSx, p: { xs: 2, sm: 2.5, md: 3.5 }, overflow: "hidden" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: hasMetrics ? "1fr auto" : "1fr",
          },
          gap: { xs: 2, md: 2.5 },
          mb: { xs: 2, md: 2.5 },
          alignItems: "start",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", mb: 1.25 }}>
            <Box
              aria-hidden
              sx={{
                width: 32,
                height: 32,
                borderRadius: "999px",
                bgcolor: "var(--portal-accent)",
                color: "#fff",
                display: "grid",
                placeItems: "center",
                fontFamily: portalFont,
                fontWeight: 700,
                fontSize: "0.875rem",
                flexShrink: 0,
                mt: 0.35,
              }}
            >
              {activation.number}
            </Box>
            <Typography
              component="h3"
              sx={{
                fontFamily: portalFont,
                fontWeight: 700,
                color: "var(--portal-text)",
                fontSize: { xs: "1.2rem", md: "1.5rem" },
                lineHeight: 1.2,
                letterSpacing: "-0.02em",
                minWidth: 0,
              }}
            >
              {activation.title}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.25,
              mb: 1.5,
              pl: { xs: 0, sm: "44px" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: { xs: 1, sm: 2 },
                rowGap: 0.75,
                alignItems: "center",
              }}
            >
              <TinyMeta
                icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 14 }} />}
                text={activation.date}
              />
              <TinyMeta
                icon={<LocationOnOutlinedIcon sx={{ fontSize: 14 }} />}
                text={activation.location}
              />
            </Box>

            {hasLineup ? (
              <Box
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: "14px",
                  bgcolor: "var(--portal-surface-raised)",
                  border: "1px solid var(--portal-border)",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: portalFont,
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: "var(--portal-text-muted)",
                    mb: 0.75,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                  }}
                >
                  <GroupsOutlinedIcon sx={{ fontSize: 14 }} />
                  Lineup
                </Typography>
                <Typography
                  sx={{
                    fontFamily: portalFont,
                    fontSize: "0.8125rem",
                    color: "var(--portal-text)",
                    lineHeight: 1.55,
                  }}
                >
                  {activation.talent}
                </Typography>
              </Box>
            ) : null}
          </Box>

          <Typography sx={{ ...bodySx, pl: { xs: 0, sm: "44px" }, maxWidth: { xs: "100%", md: 820 } }}>
            {activation.description}
          </Typography>
        </Box>

        {hasMetrics ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(3, minmax(0, 1fr))",
                sm: `repeat(${Math.min(activation.metrics.length, 4)}, minmax(88px, 1fr))`,
              },
              gap: { xs: 0.75, sm: 1 },
              width: "100%",
            }}
          >
            {activation.metrics.map((metric) => (
              <Box
                key={metric.id}
                sx={{
                  bgcolor: "var(--portal-surface-raised)",
                  border: "1px solid var(--portal-border)",
                  borderRadius: { xs: "12px", md: "14px" },
                  px: { xs: 1, sm: 1.5 },
                  py: { xs: 1, sm: 1.25 },
                  minWidth: 0,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: portalFont,
                    fontSize: { xs: "0.5625rem", sm: "0.625rem" },
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "var(--portal-text-muted)",
                    mb: 0.5,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {metric.label}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: portalFont,
                    fontWeight: 700,
                    color: "var(--portal-text)",
                    fontSize: {
                      xs: "clamp(0.8rem, 2.8vw, 1.05rem)",
                      sm: "1.05rem",
                      md: "1.25rem",
                    },
                    letterSpacing: "-0.02em",
                    lineHeight: 1.15,
                    overflowWrap: "anywhere",
                  }}
                >
                  {metric.value}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : null}
      </Box>

      {photoCount > 0 ? (
        <Box
          sx={{
            display: { xs: "flex", md: "grid" },
            gridTemplateColumns: {
              md: `repeat(${Math.min(photoCount, 3)}, 1fr)`,
            },
            gap: { xs: 1.25, md: 1.5 },
            mt: 1,
            mx: { xs: -2, sm: 0 },
            px: { xs: 2, sm: 0 },
            overflowX: { xs: "auto", md: "visible" },
            scrollSnapType: { xs: "x mandatory", md: "none" },
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {activation.photos.map((photo) => (
            <Box
              key={photo.id}
              sx={{
                flex: { xs: "0 0 78%", sm: "0 0 62%", md: "1 1 auto" },
                minWidth: 0,
                scrollSnapAlign: "start",
              }}
            >
              <Box
                component="img"
                src={photo.src}
                alt={photo.alt}
                loading="lazy"
                sx={{
                  width: "100%",
                  aspectRatio: "16 / 10",
                  objectFit: "cover",
                  borderRadius: { xs: "14px", md: "16px" },
                  display: "block",
                  bgcolor: "var(--portal-surface-raised)",
                }}
              />
              {photo.caption ? (
                <Typography
                  sx={{
                    fontFamily: portalFont,
                    fontSize: "0.8125rem",
                    color: "var(--portal-text-muted)",
                    mt: 1,
                  }}
                >
                  {photo.caption}
                </Typography>
              ) : null}
            </Box>
          ))}
        </Box>
      ) : null}

      {activation.contentLinks?.length ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 2 }}>
          {activation.contentLinks.map((link) => (
            <Link
              key={link.id}
              href={link.href}
              sx={{
                fontFamily: portalFont,
                fontWeight: 600,
                color: "var(--portal-accent)",
                fontSize: "0.875rem",
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
                "&:focus-visible": {
                  outline: "2px solid var(--portal-accent)",
                  outlineOffset: 2,
                },
              }}
            >
              {link.label}
            </Link>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

function TinyMeta({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, minWidth: 0 }}>
      <Box sx={{ color: "var(--portal-text-muted)", display: "flex", flexShrink: 0 }}>{icon}</Box>
      <Typography
        sx={{
          fontFamily: portalFont,
          fontSize: "0.8125rem",
          color: "var(--portal-text-muted)",
          lineHeight: 1.35,
        }}
      >
        {text}
      </Typography>
    </Box>
  );
}
